<template>
    <div class="main-layout__container">
        <div class="content__header content__header--one-item-per-line">
            <div class="content-header__back">
                <UIBackButton
                    :text="'К списку авто'"
                    :to="router.resolve({ name: 'CarsList' }).href"
                />
            </div>

            <div class="content-header__title">Редактирование авто</div>

            <div class="content-header__background content-header__background--add-edit-page"></div>
        </div>

        <UILoader v-if="isCarLoading" class="editable" />

        <template v-else>
            <div class="content__body">
                <div class="form__content">
                    <!-- Первый шаг -->
                    <div class="form__fields">
                        <UIBrandImageableRadio
                            v-model="formBrand"
                            :error-message="errors?.brand_id?.join() ?? ''"
                        />

                        <div class="form__field-group">
                            <UISelect
                                id="model_id"
                                v-model="formModelId"
                                :options="modelOptions"
                                label="Модель*"
                                :error-message="errors?.model_id?.join() ?? ''"
                            />

                            <UISelect
                                id="model_code_id"
                                v-model="form.model_code_id"
                                :options="modelCodeOptions"
                                label="Код модели"
                                :error-message="errors?.model_code_id?.join() ?? ''"
                            />

                            <UIInput
                                id="vin"
                                v-model="form.vin"
                                type="text"
                                label="VIN*"
                                :error-message="errors?.vin?.join() ?? ''"
                            />
                        </div>
                    </div>

                    <!-- Второй шаг -->
                    <div class="form__fields">
                        <div class="form__subtitle">Характеристики</div>

                        <div class="form__field-group">
                            <UISelect
                                id="production_year"
                                v-model="form.production_year"
                                :options="productionYearOptions"
                                label="Год выпуска*"
                                :error-message="errors?.production_year?.join() ?? ''"
                            />

                            <UISelect
                                id="body_type"
                                v-model="form.body_type"
                                :options="bodyTypeOptions"
                                label="Тип кузова*"
                                :error-message="errors?.body_type?.join() ?? ''"
                            />

                            <UIInput
                                id="body_color"
                                v-model="form.body_color"
                                label="Цвет кузова*"
                                :error-message="errors?.body_color?.join() ?? ''"
                            />

                            <UISelect
                                id="transmission_type"
                                v-model="form.transmission_type"
                                :options="transmissionTypeOptions"
                                label="Тип трансмиссии*"
                                :error-message="errors?.transmission_type?.join() ?? ''"
                            />

                            <UISelect
                                id="engine_type"
                                v-model="form.engine_type"
                                :options="engineTypeOptions"
                                label="Тип двигателя*"
                                :error-message="errors?.engine_type?.join() ?? ''"
                            />

                            <div class="form__radio-group-section">
                                <div class="radio-group-section__label">Тип привода*</div>

                                <UIRadioGroup v-model="form.drive_type">
                                    <template v-for="driveTypeOption in driveTypeOptions">
                                        <UIRadio
                                            :id="'drive_type-' + driveTypeOption.value"
                                            :value="driveTypeOption.value"
                                            :label="driveTypeOption.label"
                                        />
                                    </template>
                                </UIRadioGroup>

                                <span v-if="errors?.drive_type" class="form-field__error">
                                    {{ errors?.drive_type?.join() }}
                                </span>
                            </div>

                            <UIRange
                                id="engine_volume"
                                v-model="form.engine_volume"
                                :min="CarMinEngineVolume"
                                :max="CarMaxEngineVolume"
                                :interval="0.1"
                                label="Объем двигателя, л.*"
                                :format-numbers="false"
                                :range-mode="false"
                                :error-message="errors?.engine_volume?.join() ?? ''"
                            />

                            <UIInput
                                id="engine_power"
                                v-model="form.engine_power"
                                type="text"
                                label="Мощность двигателя, л.с.*"
                                :error-message="errors?.engine_power?.join() ?? ''"
                                only-numbers
                            />

                            <UIInput
                                id="mileage"
                                v-model="form.mileage"
                                type="text"
                                label="Пробег, км*"
                                :error-message="errors?.mileage?.join() ?? ''"
                                only-numbers
                            />
                        </div>
                    </div>

                    <!-- Третий шаг -->
                    <div class="form__fields">
                        <div class="form__subtitle">Дополнительные параметры</div>

                        <div class="form__field-group">
                            <UISelect
                                id="admission_type"
                                v-model="form.admission_type"
                                :options="admissionTypeOptions"
                                label="Тип поступления*"
                                :error-message="errors?.admission_type?.join() ?? ''"
                            />

                            <UIInput
                                id="pts_number"
                                v-model="form.pts_number"
                                type="text"
                                label="Номер ПТС*"
                                :error-message="errors?.pts_number?.join() ?? ''"
                            />

                            <UIInput
                                id="start_price"
                                v-model="form.start_price"
                                type="text"
                                label="Стартовая цена*"
                                :error-message="errors?.start_price?.join() ?? ''"
                                :format-numbers="true"
                            />
                        </div>

                        <div class="form__field">
                            <UITextArea
                                id="additional_equipment"
                                v-model="form.additional_equipment"
                                label="Дополнительное оборудование"
                                :max-length="null"
                                :height="100"
                                :resizable="false"
                                :error-message="errors?.additional_equipment?.join() ?? ''"
                            />
                        </div>

                        <div class="form__field">
                            <UICheckbox
                                v-model="form.was_owned_by_individual"
                                :error-message="errors?.was_owned_by_individual?.join() ?? ''"
                            >
                                Был в собственности у физ.лица
                            </UICheckbox>
                        </div>
                    </div>

                    <div class="form__fields">
                        <div class="form__subtitle form__subtitle--with-arrow" @click="toggleVisibleNDS">
                            <span>Тип НДС</span>

                            <span
                                class="form-subtitle__arrow"
                                :class="{ 'form-subtitle__arrow--active': isVisibleNDS }"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/arrow-temp.svg" />
                            </span>
                        </div>

                        <div v-if="isVisibleNDS" class="form__field-group">
                            <UIRadioGroup v-model="form.nds_type">
                                <template v-for="ndsTypeOption in getCarNdsTypeSelectOptions()">
                                    <UIRadio
                                        :id="'nds_type-' + ndsTypeOption.value"
                                        :value="ndsTypeOption.value"
                                        :label="ndsTypeOption.label"
                                    />
                                </template>
                            </UIRadioGroup>

                            <span v-if="errors?.nds_type" class="form-field__error">
                                {{ errors?.nds_type?.join() }}
                            </span>
                        </div>
                    </div>

                    <!-- Четвертый шаг -->
                    <div class="form__fields">
                        <div class="form__subtitle form__subtitle--with-arrow" @click="toggleVisibleCompletion">
                            <span>Комплектация</span>

                            <span
                                class="form-subtitle__arrow"
                                :class="{ 'form-subtitle__arrow--active': isVisibleCompletion }"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/arrow-temp.svg" />
                            </span>
                        </div>

                        <template v-if="isVisibleCompletion">
                            <div class="form__field-group">
                                <div class="form__radio-group-section">
                                    <div class="radio-group-section__label">Способ заполнения</div>

                                    <UIRadioGroup v-model="formComplectationType">
                                        <template v-for="complectationTypeOption in complectationTypeOptions">
                                            <UIRadio
                                                :id="'complectation_type-' + complectationTypeOption.value"
                                                :value="complectationTypeOption.value"
                                                :label="complectationTypeOption.label"
                                            />
                                        </template>
                                    </UIRadioGroup>

                                    <span v-if="formComplectationTypeErrors.length > 0" class="form-field__error">
                                        {{ formComplectationTypeErrors.join() }}
                                    </span>
                                </div>
                            </div>

                            <div
                                v-if="formComplectationType === CarUIFormComplectationType.DecodeVin
                                    || formComplectationType === CarUIFormComplectationType.ComplectationCode
                                "
                                class="form__field-group"
                            >
                                <UISimpleButton
                                    v-if="formComplectationType === CarUIFormComplectationType.DecodeVin"
                                    :loading="decodeVinLoading"
                                    @clicked="decodeVin"
                                >
                                    Декодировать VIN
                                </UISimpleButton>

                                <UISelect
                                    v-if="complectationOptions.length > 0"
                                    id="complectation_id"
                                    v-model="form.complectation_id"
                                    :options="complectationOptions"
                                    label="Код комплектации*"
                                    :disabled="formComplectationType === CarUIFormComplectationType.DecodeVin"
                                    :error-message="errors?.complectation_id?.join() ?? ''"
                                />

                                <span v-if="complectationOptions.length === 0 && formComplectationType === CarUIFormComplectationType.ComplectationCode">
                                    Для данной модели не найдены комплектации
                                </span>
                            </div>

                            <div
                                v-if="formComplectationType === CarUIFormComplectationType.CustomComplectation
                                    || form.complectation_id !== undefined
                                "
                                class="cars-step__complectation-form"
                            >
                                <ComplectationsOptionsForm v-model="form.options" />
                            </div>
                        </template>
                    </div>

                    <!-- Пятый шаг -->
                    <div class="form__fields">
                        <div class="form__subtitle form__subtitle--with-arrow" @click="toggleVisiblePhotos">
                            <span>Фотографии</span>

                            <span
                                class="form-subtitle__arrow"
                                :class="{ 'form-subtitle__arrow--active': isVisiblePhotos }"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/arrow-temp.svg" />
                            </span>
                        </div>

                        <UIMediaGallery
                            v-if="isVisiblePhotos"
                            :initial-media="formPhotos"
                            :allowed-file-types="['image/jpg', 'image/jpeg', 'image/png', 'image/webp']"
                            @media-updated="onUpdatedFormPhotos"
                        />

                        <span v-if="formSendToModerationErrors?.photos" class="form-field__error">
                            <span v-for="error in formSendToModerationErrors.photos">
                                {{ error }}
                                <br />
                            </span>
                        </span>
                    </div>

                    <div class="form__fields">
                        <div class="form__subtitle form__subtitle--with-arrow" @click="toggleVisibleVideo">
                            <span>Видео</span>

                            <span
                                class="form-subtitle__arrow"
                                :class="{ 'form-subtitle__arrow--active': isVisibleVideo }"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/arrow-temp.svg" />
                            </span>
                        </div>

                        <UIMediaList
                            v-if="isVisibleVideo"
                            :initial-media="formVideos"
                            :allowed-file-types="['video/*']"
                            :max-files="CarMaxVideos"
                            @media-updated="onUpdatedFormVideos"
                        />

                        <div
                            v-if="formSendToModerationErrors?.videos"
                            class="form-field__error"
                        >
                            <div v-for="error in formSendToModerationErrors.videos">
                                {{ error }}
                            </div>
                        </div>
                    </div>

                    <!-- Шестой шаг -->
                    <div class="form__fields">
                        <div class="form__subtitle form__subtitle--with-arrow" @click="toggleVisibleDiagnostics">
                            <span>Диагностика и повреждения</span>

                            <span
                                class="form-subtitle__arrow"
                                :class="{ 'form-subtitle__arrow--active': isVisibleDiagnostics }"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/arrow-temp.svg" />
                            </span>
                        </div>

                        <div v-if="isVisibleDiagnostics" class="form__radio-group-section">
                            <div class="radio-group-section__label">Повреждения</div>

                            <UIRadioGroup v-model="damageRadioValue">
                                <UIRadio
                                    :id="'hasDamage'"
                                    :value="1"
                                    label="Есть"
                                    :disabled="damageRadioValue === 1"
                                />

                                <UIRadio
                                    :id="'noDamage'"
                                    :value="2"
                                    label="Нет"
                                    :disabled="damageRadioValue === 1"
                                />
                            </UIRadioGroup>
                        </div>

                        <template v-if="isVisibleDiagnostics">
                            <UIDamageList
                                v-show="formDamages.length > 0"
                                :initial-damages="initialFormDamages"
                                :errors="(errors?.updated_car_damages ?? []).concat(errors?.new_car_damages ?? [])"
                                @damage-updated="onUpdatedFormDamages"
                            />

                            <div class="cars-step__document">
                                <div class="cars-step__document-title">Диагностическая карта</div>

                                <UIMediaList
                                    :initial-media="formDocuments.diagnostic_card"
                                    :allowed-file-types="['application/pdf', 'image/*']"
                                    :max-files="CarMaxDocuments"
                                    @media-updated="onUpdatedDiagnosticCardDocuments"
                                />
                            </div>

                            <div class="cars-step__document">
                                <div class="cars-step__document-title">Отчет автокод/автотека</div>

                                <UIMediaList
                                    :initial-media="formDocuments.avtocod_report"
                                    :allowed-file-types="['application/pdf', 'image/*']"
                                    :max-files="CarMaxDocuments"
                                    @media-updated="onUpdatedAvtocodReportDocuments"
                                />
                            </div>
                        </template>
                    </div>

                    <div class="form__fields">
                        <div class="form__subtitle form__subtitle--with-arrow" @click="toggleVisibleDocuments">
                            <span>Документы</span>

                            <span
                                class="form-subtitle__arrow"
                                :class="{ 'form-subtitle__arrow--active': isVisibleDocuments }"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/arrow-temp.svg" />
                            </span>
                        </div>

                        <UIMediaList
                            v-if="isVisibleDocuments"
                            :initial-media="formDocuments.document"
                            :allowed-file-types="['application/pdf', 'image/*']"
                            :max-files="CarMaxDocuments"
                            @media-updated="onUpdatedAdditionalDocuments"
                        />

                        <div v-if="formSendToModerationErrors?.documents" class="form-field__error">
                            <div v-for="error in formSendToModerationErrors.documents">
                                {{ error }}
                            </div>
                        </div>
                    </div>

                    <!-- Седьмой шаг -->
                    <div class="form__fields">
                        <div class="form__subtitle form__subtitle--with-arrow" @click="toggleVisibleJuridical">
                            <span>Юридическая чистота</span>

                            <span
                                class="form-subtitle__arrow"
                                :class="{ 'form-subtitle__arrow--active': isVisibleJuridical }"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/arrow-temp.svg" />
                            </span>
                        </div>

                        <div v-if="isVisibleJuridical" class="form__field-group">
                            <UICheckbox
                                v-model="form.is_not_stolen"
                                :error-message="errors?.is_stolen?.join() ?? ''"
                            >
                                Автомобиль не числится в угоне
                            </UICheckbox>

                            <UICheckbox
                                v-model="form.is_not_pledged"
                                :error-message="errors?.is_pledged?.join() ?? ''"
                            >
                                Автомобиль не находится в залоге
                            </UICheckbox>

                            <UICheckbox
                                v-model="form.not_exist_dtp"
                                :error-message="errors?.exist_dtp?.join() ?? ''"
                            >
                                Нет информации о ДТП
                            </UICheckbox>

                            <UIInput
                                id="owners_count"
                                v-model="form.owners_count"
                                type="text"
                                label="Кол-во собственников"
                                :error-message="errors?.owners_count?.join() ?? ''"
                                only-numbers
                            />

                            <UIInput
                                id="months_with_last_owner"
                                v-model="form.months_with_last_owner"
                                type="text"
                                label="Месяцев у послед. собственника"
                                :error-message="errors?.months_with_last_owner?.join() ?? ''"
                                only-numbers
                            />
                        </div>
                    </div>

                    <div class="form__fields">
                        <div class="form__subtitle form__subtitle--with-arrow" @click="toggleVisibleAdditional">
                            <span>Дополнительно</span>

                            <span
                                class="form-subtitle__arrow"
                                :class="{ 'form-subtitle__arrow--active': isVisibleAdditional }"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/arrow-temp.svg" />
                            </span>
                        </div>

                        <template v-if="isVisibleAdditional">
                            <UITextArea
                                id="comment"
                                v-model="form.comment"
                                label="Описание для анонса"
                                :max-length="null"
                                :height="100"
                                :resizable="false"
                                :error-message="errors?.comment?.join() ?? ''"
                            />

                            <UIInput
                                id="tags"
                                v-model="form.tags"
                                type="text"
                                label="Теги через запятую"
                                :error-message="errors?.tags?.join() ?? ''"
                            />
                        </template>
                    </div>
                </div>
            </div>

            <div
                v-if="canSendCarToModeration(car as Car)"
                class="cars-step-edit__action-rule_accept"
            >
                <UICheckbox
                    v-model="formSendToModeration.auction_rules_accepted"
                    :error-message="formSendToModerationErrors?.auction_rules_accepted?.join() ?? ''"
                >
                    С <RulesLink>правилами аукциона</RulesLink> ознакомлен. Обязуемся оплатить вознаграждение за размещение автомобиля на аукционе в полном объеме
                </UICheckbox>
            </div>

            <div class="content__footer">
                <div class="form__actions form__actions--2-blocks">
                    <div>
                        <UISimpleButton
                            v-if="canDeleteCar(car as Car)"
                            button-type="danger"
                            @clicked="handleDeleteCar"
                        >
                            <template #icon>
                                <img v-svg-inline src="@/assets/icons/ui/trash.svg" />
                            </template>
                            Удалить авто
                        </UISimpleButton>
                    </div>

                    <div class="form__actions">
                        <UISimpleButton
                            button-type="outlined"
                            :loading="saveLoading"
                            @clicked="saveCarAsDraft"
                        >
                            Сохранить как черновик
                        </UISimpleButton>

                        <UISimpleButton
                            v-if="canSendCarToModeration(car as Car)"
                            :loading="sendToModerationLoading"
                            @clicked="sendCarToModeration"
                        >
                            Отправить на согласование
                        </UISimpleButton>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script lang="ts" setup>
    import { computed, h, inject, nextTick, onMounted, ref, watch, WatchHandle } from 'vue';
    import { useRoute, useRouter } from 'vue-router';

    import { ModalPlugin } from '@/plugins/modalPlugin.ts';

    import { DamageItem, MediaItem, SelectOption } from '@/types/form.type.ts';
    import {
        Car,
        CarCreateFormErrorsMessages,
        CarDamage,
        CarDamageCreateForm,
        CarDamageUpdateForm,
        CarDecodedComplectation,
        CarDocument,
        CarDocumentType,
        CarPhoto,
        CarPhotoType,
        CarSendToModerationErrors,
        CarUICreateFormStep,
        CarUIFormComplectationType,
        CarVideo,
        CarMinProductionYear,
        CarMaxProductionYear,
        CarMinEngineVolume,
        CarMaxEngineVolume,
        CarMaxVideos,
        CarMaxDocuments,
        getCarAdmissionTypeSelectOptions,
        getCarBodyTypeSelectOptions,
        getCarDriveTypeSelectOptions,
        getCarEngineTypeSelectOptions,
        getCarNdsTypeSelectOptions,
        getCarPhotoTypeLabel,
        getCarTransmissionTypeSelectOptions,
        getCarUIFormComplectationTypeSelectOptions,
        CarUIUpdateForm,
        CarUpdateFormErrors,
        fillCarUIUpdateFormFromCar,
        makeCarEmptyUIUpdateForm,
        mapCarUIUpdateFormToRequestUpdateForm,
        CarSendForModerationForm,
    } from '@/types/car.type.ts';
    import { Complectation, fillComplectationUIUpdateFormFromRequestComplectation } from '@/types/complectation.type.ts';
    import { Model } from '@/types/model.type.ts';
    import { ModelCode } from '@/types/model-code.type.ts';

    import useComplectationRepository from '@/repositories/complectation/useComplectationRepository.ts';
    import useCarRepository from '@/repositories/car/useCarRepository.ts';
    import useLoading from '@/utils/useLoading.ts';
    import useModelRepository from '@/repositories/model/useModelRepository.ts';
    import useNotifications from '@/utils/useNotifications.ts';
    import useUnsavedDataWarning from '@/plugins/useUnsavedDataWarning.ts';

    import UIBackButton from '@/components/ui/UIBackButton/UIBackButton.vue';
    import UIBrandImageableRadio from '@/components/ui/UIBrandImageableRadio/UIBrandImageableRadio.vue';
    import UICheckbox from '@/components/ui/UICheckbox/UICheckbox.vue';
    import UIDamageList from '@/components/ui/UIDamageList/UIDamageList.vue';
    import UIInput from '@/components/ui/UIInput/UIInput.vue';
    import UILoader from '@/components/ui/UILoader/UILoader.vue';
    import UISelect from '@/components/ui/UISelect/UISelect.vue';
    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';
    import UIMediaGallery from '@/components/ui/UIMediaGallery/UIMediaGallery.vue';
    import UIMediaList from '@/components/ui/UIMediaList/UIMediaList.vue';
    import UIRadio from '@/components/ui/UIRadio/UIRadio.vue';
    import UIRadioGroup from '@/components/ui/UIRadioGroup/UIRadioGroup.vue';
    import UIRange from '@/components/ui/UIRange/UIRange.vue';
    import UITextArea from '@/components/ui/UITextArea/UITextArea.vue';

    import ConfirmActionModal from '@/components/modals/ConfirmActionModal/ConfirmActionModal.vue';
    import RulesLink from '@/components/shared/RulesLink/RulesLink.vue';

    import CarConfirmDelete from '@/components/cars/CarConfirmDelete/CarConfirmDelete.vue';
    import ComplectationsOptionsForm from '@/components/complectations/ComplectationsOptionsForm/ComplectationsOptionsForm.vue';
    import usePermission from '@/repositories/auth/usePermission.ts';

    const $modal = inject('$modal') as typeof ModalPlugin;

    const router = useRouter();
    const route = useRoute();
    const { getCar, getComplectationByVin, updateCar, sendToModerationCar, deleteCar } = useCarRepository();
    const { getAllActiveModels } = useModelRepository();
    const { getComplectationsByModelId, getComplectation } = useComplectationRepository();
    const { canDeleteCar, canSendCarToModeration } = usePermission();
    const { isLoading: isCarLoading, setLoading: setCarLoading } = useLoading();
    const { showSuccess } = useNotifications();
    const { setDirty } = useUnsavedDataWarning();

    const form = ref<CarUIUpdateForm>(makeCarEmptyUIUpdateForm());
    const formComplectationType = ref<CarUIFormComplectationType | undefined>(undefined);
    const formPhotos = ref<MediaItem[]>([]);
    const formVideos = ref<MediaItem[]>([]);
    const initialFormDamages = ref<DamageItem[]>([]);
    const formDamages = ref<DamageItem[]>([]);
    const formDocuments = ref<Record<CarDocumentType, MediaItem[]>>({
        diagnostic_card: [],
        avtocod_report: [],
        document: [],
    });
    const formSendToModeration = ref<CarSendForModerationForm>({
        auction_rules_accepted: false,
    });

    const productionYearOptions = ref<SelectOption[]>([]);
    const bodyTypeOptions = ref<SelectOption[]>(getCarBodyTypeSelectOptions());
    const transmissionTypeOptions = ref<SelectOption[]>(getCarTransmissionTypeSelectOptions());
    const engineTypeOptions = ref<SelectOption[]>(getCarEngineTypeSelectOptions());
    const driveTypeOptions = ref<SelectOption[]>(getCarDriveTypeSelectOptions());
    const admissionTypeOptions = ref<SelectOption[]>(getCarAdmissionTypeSelectOptions());
    // const complectationTypeOptions = ref<SelectOption[]>(getCarUIFormComplectationTypeSelectOptions());
    const complectationTypeOptions = ref<SelectOption[]>(
        getCarUIFormComplectationTypeSelectOptions()
            .filter((complectationOption) => complectationOption.value !== CarUIFormComplectationType.DecodeVin),
    );
    const complectationOptions = ref<SelectOption[]>([]);

    const errors = ref<CarUpdateFormErrors | null>(null);
    const formComplectationTypeErrors = ref<string[]>([]);
    const formSendToModerationErrors = ref<CarSendToModerationErrors | null>(null);

    const saveLoading = ref<boolean>(false);
    const sendToModerationLoading = ref<boolean>(false);
    const decodeVinLoading = ref<boolean>(false);

    const arModels = ref<Model[]>([]);

    const isVisibleNDS = ref(true);
    const isVisiblePhotos = ref(true);
    const isVisibleCompletion = ref(true);
    const isVisibleVideo = ref(true);
    const isVisibleDiagnostics = ref(true);
    const isVisibleDocuments = ref(true);
    const isVisibleJuridical = ref(true);
    const isVisibleAdditional = ref(true);

    const damageRadioValue = computed({
        get: () => formDamages.value.length > 0 ? 1 : 2,
        set: (newValue) => {
            if (newValue === 1 && formDamages.value.length === 0) {
                formDamages.value.push({
                    id: Date.now(),
                    selectedElement: undefined,
                    selectedDamageType: undefined,
                    file: null,
                    coatingThickness: '',
                    userComment: '',
                    previewImage: null,
                });
            }
        },
    });

    const formBrand = computed<string | undefined>({
        get() {
            if (form.value.brand_id === 1) {
                return 'hyundai';
            }

            if (form.value.brand_id === 2) {
                return 'genesis';
            }

            return undefined;
        },
        set(brandValue: string | undefined) {
            form.value.brand_id = undefined;

            if (brandValue === 'hyundai') {
                form.value.brand_id = 1;
            }
            else if (brandValue === 'genesis') {
                form.value.brand_id = 2;
            }

            if (formModelId.value !== undefined) {
                const selectedModel = arModels.value.find((m: Model) => m.id === formModelId.value);
                formModelId.value = selectedModel?.brand_id === form.value.brand_id ? selectedModel?.id : undefined;
            }
        },
    });

    const formModelId = computed<number | undefined>({
        get: () => form.value.model_id,
        set: (modelId?: number) => {
            form.value.model_id = modelId;

            if (modelId === undefined) {
                form.value.model_code_id = undefined;
            } else {
                const selectedModel = arModels.value.find((m: Model) => m.id === modelId);
                const foundCode = selectedModel?.model_codes?.find((mc: ModelCode) => mc.id === form.value.model_code_id);
                form.value.model_code_id = foundCode?.id;
            }

            fetchComplectations();
        },
    });

    const modelOptions = computed<SelectOption[]>(() => {
        return arModels.value
            .filter((m: Model) => m.brand_id === form.value.brand_id)
            .map((m: Model): SelectOption => ({
                label: m.name,
                value: m.id,
            }));
    });

    const modelCodeOptions = computed<SelectOption[]>(() => {
        const model = arModels.value.find((model: Model) => model.id === form.value.model_id);

        return model?.model_codes?.map((modelCode: ModelCode): SelectOption => ({
            label: modelCode.model_code,
            value: modelCode.id,
        })) ?? [];
    });

    const carId = route.params.car_id as unknown as number;
    let car: Car | null = null;
    let complectationLoaded = false;
    let complectationOptionsWatchHandler: WatchHandle | undefined = undefined;

    watch(form, () => {
        setDirty(true);
    }, { deep: true });

    watch(
        () => formComplectationType.value,
        () => fetchComplectations(),
    );

    watch(
        () => form.value.complectation_id,
        () => fillComplectationOptions(),
    );

    function saveCarAsDraft(): void {
        saveLoading.value = true;

        saveCar()
            .then(() => {
                showSuccess({
                    title: 'Изменения в автомобиле сохранены',
                });

                goToCarDetailPage(carId);
            })
            .finally(() => {
                saveLoading.value = false;
            });
    }

    function sendCarToModeration(): void {
        sendToModerationLoading.value = true;
        formSendToModerationErrors.value = null;

        saveCar()
            .then(() => {
                sendToModerationCar(carId, formSendToModeration.value)
                    .then(() => {
                        showSuccess({
                            title: 'Автомобиль отправлен на согласование',
                        });

                        goToCarDetailPage(carId);
                    })
                    .catch((moderationErrors: CarSendToModerationErrors) => {
                        formSendToModerationErrors.value = moderationErrors;
                    })
                    .finally(() => {
                        sendToModerationLoading.value = false;
                    });
            })
            .catch(() => {
                sendToModerationLoading.value = false;
            });
    }

    function handleDeleteCar(): void {
        $modal.open({
            component: ConfirmActionModal,
            modalType: 'narrow',
            slots: {
                default: () =>
                    car ? h(CarConfirmDelete, { car }) : '',
            },
            props: {
                onConfirm: async () => {
                    await deleteCar(carId);

                    showSuccess({
                        title: 'Автомобиль удален',
                    });

                    router.push({ name: 'CarsList' });
                },
            },
        });
    }

    function decodeVin(): void {
        decodeVinLoading.value = true;

        formComplectationTypeErrors.value = [];
        getComplectationByVin(form.value.vin)
            .then((complectation: CarDecodedComplectation) => {
                complectationOptions.value = [
                    {
                        label: complectation.code,
                        value: complectation.id,
                    },
                ];

                form.value.complectation_id = complectation.id;
            })
            .catch(() => {
                formComplectationTypeErrors.value = [
                    'Для данного VIN код комплектации не найден. Выберите нужный код комплектации или заполните свою комплектацию вручную',
                ];
            })
            .finally(() => {
                decodeVinLoading.value = false;
            });
    }

    function onUpdatedFormPhotos(updatedMedia: MediaItem[]): void {
        form.value.new_photos = {};
        form.value.new_additional_photos = [];
        form.value.deleted_photos = [];
        form.value.deleted_additional_photos = [];
        form.value.updated_photos = [];
        form.value.updated_additional_photos = [];

        function deletePhoto(media: MediaItem): void {
            if (!media.apiId) {
                return;
            }

            if (media.type === CarPhotoType.Additional) {
                form.value.deleted_additional_photos.push(media.apiId);
            }
            else {
                form.value.deleted_photos.push(media.apiId);
            }
        }

        function newPhoto(media: MediaItem): void {
            if (media.type === CarPhotoType.Additional) {
                form.value.new_additional_photos.push({
                    file: media.file,
                    rotation: media.rotation,
                });
            }
            else {
                form.value.new_photos[media.type as CarPhotoType] = {
                    file: media.file,
                    rotation: media.rotation,
                };
            }
        }

        function updatePhoto(media: MediaItem): void {
            if (media.apiId === undefined) {
                return;
            }

            if (media.type === CarPhotoType.Additional) {
                form.value.updated_additional_photos.push({
                    id: media.apiId,
                    rotation: media.rotation,
                });
            }
            else {
                form.value.updated_photos.push({
                    id: media.apiId,
                    rotation: media.rotation,
                });
            }
        }

        updatedMedia.forEach((newMedia: MediaItem) => {
            if (newMedia.apiId) {
                if (newMedia.file || newMedia.previewUrl === '') {
                    deletePhoto(newMedia);
                }

                if (newMedia.file === null) {
                    const oldMedia: MediaItem | undefined = formPhotos.value.find((media: MediaItem) => media.apiId === newMedia.apiId);
                    const changed = oldMedia !== undefined && oldMedia.rotation !== newMedia.rotation;
                    if (changed) {
                        updatePhoto(newMedia);
                    }
                }
            }

            if (newMedia.file) {
                newPhoto(newMedia);
            }
        });

        formPhotos.value.forEach((oldMedia: MediaItem) => {
            if (!oldMedia.apiId) {
                return;
            }

            const newMedia: MediaItem | undefined = updatedMedia.find((media: MediaItem) => media.apiId === oldMedia.apiId);
            const deleted = newMedia === undefined || newMedia.previewUrl === '';
            if (deleted) {
                deletePhoto(oldMedia);
            }
        });
    }

    function onUpdatedFormVideos(updatedMedia: MediaItem[]): void {
        form.value.new_car_videos = [];
        form.value.deleted_videos = [];

        updatedMedia.forEach((newVideo: MediaItem) => {
            if (newVideo.apiId) {
                if (newVideo.file) {
                    form.value.deleted_videos.push(newVideo.apiId);
                    form.value.new_car_videos.push(newVideo.file);
                }
            }
            else {
                if (newVideo.file) {
                    form.value.new_car_videos.push(newVideo.file);
                }
            }
        });

        formVideos.value.forEach((oldVideo: MediaItem) => {
            if (!oldVideo.apiId) {
                return;
            }

            const newVideo = updatedMedia.find((video: MediaItem) => video.apiId === oldVideo.apiId);

            if (!newVideo) {
                form.value.deleted_videos.push(oldVideo.apiId);
            }
        });
    }

    function onUpdatedFormDamages(updatedDamages: DamageItem[]): void {
        form.value.new_car_damages = [];
        form.value.updated_car_damages = [];
        form.value.deleted_car_damages = [];

        updatedDamages.forEach((newDamage: DamageItem) => {
            if (newDamage.apiId) {
                const oldDamage = initialFormDamages.value.find((damage: DamageItem) => damage.apiId === newDamage.apiId);

                if (oldDamage) {
                    const changed =
                        oldDamage.selectedElement !== newDamage.selectedElement ||
                        oldDamage.selectedDamageType !== newDamage.selectedDamageType ||
                        oldDamage.coatingThickness !== newDamage.coatingThickness ||
                        oldDamage.userComment !== newDamage.userComment ||
                        newDamage.file;

                    if (changed) {
                        const damage: CarDamageUpdateForm = {
                            id: newDamage.apiId,
                            deleted_photo_id: null,
                        };

                        if (newDamage.selectedElement) {
                            damage.symbolic_code = newDamage.selectedElement;
                        }

                        if (newDamage.selectedDamageType) {
                            damage.type = newDamage.selectedDamageType;
                        }

                        if (newDamage.coatingThickness) {
                            damage.paintwork_thickness = parseInt(newDamage.coatingThickness);
                        }

                        if (newDamage.userComment) {
                            damage.comment = newDamage.userComment;
                        }

                        if (newDamage.file) {
                            damage.photo = newDamage.file;
                            damage.deleted_photo_id = newDamage.apiPhotoId ?? null;
                        }

                        form.value.updated_car_damages.push(damage);
                    }
                }
            }
            else {
                const damage: CarDamageCreateForm = {};

                if (newDamage.selectedElement) {
                    damage.symbolic_code = newDamage.selectedElement;
                }

                if (newDamage.selectedDamageType) {
                    damage.type = newDamage.selectedDamageType;
                }

                if (newDamage.coatingThickness) {
                    damage.paintwork_thickness = parseInt(newDamage.coatingThickness);
                }

                if (newDamage.userComment) {
                    damage.comment = newDamage.userComment;
                }

                if (newDamage.file) {
                    damage.photo = newDamage.file;
                }

                form.value.new_car_damages.push(damage);
            }
        });

        initialFormDamages.value.forEach((oldDamage: DamageItem) => {
            if (!oldDamage.apiId) {
                return;
            }

            const newDamage = updatedDamages.find((damage: DamageItem) => damage.apiId === oldDamage.apiId);
            if (!newDamage) {
                form.value.deleted_car_damages.push(oldDamage.apiId);
            }
        });

        formDamages.value = updatedDamages;
    }

    function onUpdatedCarDocumentsByType(type: CarDocumentType, updatedMedia: MediaItem[]): void {
        form.value.new_documents[type] = [];
        form.value.deleted_documents = form.value.deleted_documents.filter((documentId: number) => {
            return !formDocuments.value[type].find((document: MediaItem) => document.apiId === documentId);
        });

        updatedMedia.forEach((newDocument: MediaItem) => {
            if (newDocument.apiId) {
                if (newDocument.file) {
                    form.value.deleted_documents.push(newDocument.apiId);
                    form.value.new_documents[type]?.push(newDocument.file);
                }
            }
            else {
                if (newDocument.file) {
                    form.value.new_documents[type]?.push(newDocument.file);
                }
            }
        });

        formDocuments.value[type].forEach((oldDocument: MediaItem) => {
            if (!oldDocument.apiId) {
                return;
            }

            const newDocument = updatedMedia.find((document: MediaItem) => document.apiId === oldDocument.apiId);
            if (!newDocument) {
                form.value.deleted_documents.push(oldDocument.apiId);
            }
        });
    }

    function onUpdatedDiagnosticCardDocuments(updatedMedia: MediaItem[]): void {
        onUpdatedCarDocumentsByType(CarDocumentType.DiagnosticCard, updatedMedia);
    }

    function onUpdatedAvtocodReportDocuments(updatedMedia: MediaItem[]): void {
        onUpdatedCarDocumentsByType(CarDocumentType.AvtocodReport, updatedMedia);
    }

    function onUpdatedAdditionalDocuments(updatedMedia: MediaItem[]): void {
        onUpdatedCarDocumentsByType(CarDocumentType.Document, updatedMedia);
    }

    const toggleVisibleNDS = (): void => {
        isVisibleNDS.value = !isVisibleNDS.value;
    };

    const toggleVisiblePhotos = (): void => {
        isVisiblePhotos.value = !isVisiblePhotos.value;
    };

    const toggleVisibleCompletion = (): void => {
        isVisibleCompletion.value = !isVisibleCompletion.value;
    };

    const toggleVisibleVideo = (): void => {
        isVisibleVideo.value = !isVisibleVideo.value;
    };

    const toggleVisibleDiagnostics = (): void => {
        isVisibleDiagnostics.value = !isVisibleDiagnostics.value;
    };

    const toggleVisibleDocuments = (): void => {
        isVisibleDocuments.value = !isVisibleDocuments.value;
    };

    const toggleVisibleJuridical = (): void => {
        isVisibleJuridical.value = !isVisibleJuridical.value;
    };

    const toggleVisibleAdditional = (): void => {
        isVisibleAdditional.value = !isVisibleAdditional.value;
    };

    function saveCar(): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!validate()) {
                reject();

                return;
            }

            updateCar(carId, mapCarUIUpdateFormToRequestUpdateForm(form.value))
                .then(() => {
                    setDirty(false);
                    resolve();
                })
                .catch((updateErrors: CarUpdateFormErrors) => {
                    errors.value = updateErrors;
                    reject();
                });
        });
    }

    function validate(): boolean {
        let isValid = true;
        errors.value = {};

        Object.values(CarUICreateFormStep).forEach((step: CarUICreateFormStep | string) => {
            if (typeof step === 'string') {
                return;
            }

            if (!validateStep(step)) {
                isValid = false;
            }
        });

        return isValid;
    }

    function validateStep(step: CarUICreateFormStep): boolean {
        switch (step) {
            case CarUICreateFormStep.Common:
                return validateCommonStep();
            case CarUICreateFormStep.Characteristics:
                return validateCharacteristicsStep();
            case CarUICreateFormStep.AdditionalParameters:
                return validateAdditionalParametersStep();
            case CarUICreateFormStep.Complectation:
                return validateComplectationStep();
            case CarUICreateFormStep.Media:
                return validateMediaStep();
            case CarUICreateFormStep.Damages:
                return validateDamagesStep();
            case CarUICreateFormStep.Legal:
                return validateLegalStep();
        }
    }

    function validateCommonStep(): boolean {
        let isValid: boolean = true;

        if (errors.value === null) {
            errors.value = {};
        }

        if (form.value.brand_id === undefined) {
            errors.value.brand_id = [CarCreateFormErrorsMessages.required];
            isValid = false;
        }

        if (form.value.model_id === undefined) {
            errors.value.model_id = [CarCreateFormErrorsMessages.required];
            isValid = false;
        }

        if (form.value.vin.length === 0) {
            errors.value.vin = [CarCreateFormErrorsMessages.required];
            isValid = false;
        }

        return isValid;
    }

    function validateCharacteristicsStep(): boolean {
        let isValid: boolean = true;

        if (errors.value === null) {
            errors.value = {};
        }

        if (form.value.production_year === undefined) {
            errors.value.production_year = [CarCreateFormErrorsMessages.required];
            isValid = false;
        }

        if (form.value.body_type === undefined) {
            errors.value.body_type = [CarCreateFormErrorsMessages.required];
            isValid = false;
        }

        if (form.value.body_color.length === 0) {
            errors.value.body_color = [CarCreateFormErrorsMessages.required];
            isValid = false;
        }

        if (form.value.transmission_type === undefined) {
            errors.value.transmission_type = [CarCreateFormErrorsMessages.required];
            isValid = false;
        }

        if (form.value.engine_type === undefined) {
            errors.value.engine_type = [CarCreateFormErrorsMessages.required];
            isValid = false;
        }

        if (form.value.drive_type === undefined) {
            errors.value.drive_type = [CarCreateFormErrorsMessages.required];
            isValid = false;
        }

        if (form.value.engine_volume === 0) {
            errors.value.engine_volume = [CarCreateFormErrorsMessages.required];
            isValid = false;
        }

        if (form.value.engine_power.length === 0) {
            errors.value.engine_power = [CarCreateFormErrorsMessages.required];
            isValid = false;
        }
        else if (!Number.isInteger(Number(form.value.engine_power.length))) {
            errors.value.engine_power = [CarCreateFormErrorsMessages.integer];
            isValid = false;
        }

        if (form.value.mileage.length === 0) {
            errors.value.mileage = [CarCreateFormErrorsMessages.required];
            isValid = false;
        }
        else if (!Number.isInteger(Number(form.value.mileage.length))) {
            errors.value.mileage = [CarCreateFormErrorsMessages.integer];
            isValid = false;
        }

        return isValid;
    }

    function validateAdditionalParametersStep(): boolean {
        let isValid: boolean = true;

        if (errors.value === null) {
            errors.value = {};
        }

        if (form.value.admission_type === undefined) {
            errors.value.admission_type = [CarCreateFormErrorsMessages.required];
            isValid = false;
        }

        if (form.value.pts_number.length === 0) {
            errors.value.pts_number = [CarCreateFormErrorsMessages.required];
            isValid = false;
        }

        if (form.value.start_price.length === 0) {
            errors.value.start_price = [CarCreateFormErrorsMessages.required];
            isValid = false;
        }
        else if (!Number.isInteger(Number(form.value.start_price))) {
            errors.value.start_price = [CarCreateFormErrorsMessages.integer];
            isValid = false;
        }

        if (form.value.nds_type === undefined) {
            errors.value.nds_type = [CarCreateFormErrorsMessages.required];
            isValid = false;
        }

        return isValid;
    }

    function validateComplectationStep(): boolean {
        let isValid: boolean = true;

        if (errors.value === null) {
            errors.value = {};
            formComplectationTypeErrors.value = [];
        }

        if (formComplectationType.value === undefined) {
            formComplectationTypeErrors.value = [CarCreateFormErrorsMessages.required];
            isValid = false;
        }
        else if (formComplectationType.value === CarUIFormComplectationType.DecodeVin && form.value.complectation_id === undefined) {
            formComplectationTypeErrors.value = [CarCreateFormErrorsMessages.vinDecodingRequired];
            isValid = false;
        }
        else if (formComplectationType.value !== CarUIFormComplectationType.CustomComplectation && form.value.complectation_id === undefined) {
            errors.value.complectation_id = [CarCreateFormErrorsMessages.required];
            isValid = false;
        }

        return isValid;
    }

    function validateMediaStep(): boolean {
        let isValid: boolean = true;

        if (errors.value === null) {
            errors.value = {};
        }

        return isValid;
    }

    function validateDamagesStep(): boolean {
        let isValid: boolean = true;

        if (errors.value === null) {
            errors.value = {};
        }

        return isValid;
    }

    function validateLegalStep(): boolean {
        let isValid: boolean = true;

        if (errors.value === null) {
            errors.value = {};
        }

        return isValid;
    }

    function fillFormMedia(car: Car): void {
        fillFormPhotos(car);
        fillFormVideos(car);
    }

    function fillFormPhotos(car: Car): void {
        const photos: MediaItem[] = [];

        const carPhotosByType: Record<string, CarPhoto> = {};
        const carAdditionalPhotos: CarPhoto[] = [];

        car.photos?.forEach((photo: CarPhoto) => {
            if (photo.type === CarPhotoType.Additional) {
                carAdditionalPhotos.push(photo);
            }
            else {
                carPhotosByType[photo.type] = photo;
            }
        });

        Object.values(CarPhotoType).forEach((type: CarPhotoType) => {
            if (type === CarPhotoType.Additional) {
                return;
            }

            photos.push({
                id: type,
                type: type,
                label: getCarPhotoTypeLabel(type),
                previewUrl: carPhotosByType[type]?.path_small ?? '',
                file: null,
                rotation: 0,
                apiId: carPhotosByType[type]?.id ?? undefined,
            });
        });

        carAdditionalPhotos.forEach((photo: CarPhoto, index) => {
            photos.push({
                id: 'additional-photo.' + index,
                type: CarPhotoType.Additional,
                label: getCarPhotoTypeLabel(CarPhotoType.Additional),
                previewUrl: photo.path_small,
                file: null,
                rotation: 0,
                apiId: photo.id,
            });
        });

        for (let index = carAdditionalPhotos.length; index < 2; index++) {
            photos.push({
                id: 'additional-photo.' + index,
                type: CarPhotoType.Additional,
                label: getCarPhotoTypeLabel(CarPhotoType.Additional),
                previewUrl: '',
                file: null,
                rotation: 0,
            });
        }

        formPhotos.value = photos;
    }

    function fillFormVideos(car: Car): void {
        car.videos?.forEach((video: CarVideo, index): void => {
            formVideos.value.push({
                id: 'video.' + index,
                type: video.content_type,
                label: video.name,
                previewUrl: video.path,
                file: null,
                rotation: 0,
                apiId: video.id,
            });
        });
    }

    function fillFormDamages(car: Car): void {
        initialFormDamages.value = [];
        car.damages?.forEach((damage: CarDamage, index) => {
            initialFormDamages.value.push({
                id: index,
                selectedElement: damage.element,
                selectedDamageType: damage.type,
                coatingThickness: `${damage.paintwork_thickness ?? ''}`,
                userComment: damage.comment ?? '',
                previewImage: damage.photo?.path_small ?? null,
                file: null,
                apiId: damage.id,
                apiPhotoId: damage.photo?.id ?? undefined,
            });
        });
        formDamages.value = initialFormDamages.value;
    }

    function fillFormDocuments(car: Car): void {
        formDocuments.value.document = [];
        formDocuments.value.avtocod_report = [];
        formDocuments.value.diagnostic_card = [];

        car.documents?.forEach((document: CarDocument, index) => {
            formDocuments.value[document.type].push({
                id: document.type + '.' + index,
                type: document.content_type,
                label: document.name,
                previewUrl: '',
                file: null,
                rotation: 0,
                apiId: document.id,
            });
        });
    }

    function fillComplectationOptions(): void {
        if (form.value.complectation_id !== undefined) {
            getComplectation(form.value.complectation_id).then((complectation: Complectation) => {
                complectationLoaded = false;
                const complectationUIUpdateForm = fillComplectationUIUpdateFormFromRequestComplectation(complectation);
                form.value.options = complectationUIUpdateForm.complectation_option;
            });
        }
    }

    function watchComplectationOptions(): void {
        if (complectationOptionsWatchHandler !== undefined) {
            complectationOptionsWatchHandler.stop();
        }

        complectationOptionsWatchHandler = watch(
            () => form.value.options,
            () => handleComplectationChange(),
            { deep: true },
        );
    }

    function handleComplectationChange(): void {
        if (complectationLoaded && formComplectationType.value !== CarUIFormComplectationType.CustomComplectation) {
            formComplectationType.value = CarUIFormComplectationType.CustomComplectation;
            complectationOptions.value = [];
            form.value.complectation_id = undefined;
        }

        complectationLoaded = true;
    }

    function goToCarDetailPage(carId: number): void {
        router.push({
            name: 'ViewCar',
            params: {
                car_id: carId,
            },
        });
    }

    function fetchCar(): void {
        setCarLoading(true);

        getCar(carId).then((fetchedCar: Car) => {
            car = fetchedCar;
            form.value = fillCarUIUpdateFormFromCar(fetchedCar);

            formComplectationType.value = fetchedCar.complectation.id
                ? CarUIFormComplectationType.ComplectationCode
                : CarUIFormComplectationType.CustomComplectation;

            fillFormMedia(fetchedCar);
            fillFormDamages(fetchedCar);
            fillFormDocuments(fetchedCar);

            nextTick().then(() => {
                watchComplectationOptions();
            });

            setCarLoading(false);
        });
    }

    function fetchComplectations(): void {
        formComplectationTypeErrors.value = [];
        complectationLoaded = false;

        switch (formComplectationType.value) {
            case CarUIFormComplectationType.DecodeVin:
                complectationOptions.value = [];
                form.value.complectation_id = undefined;
                break;
            case CarUIFormComplectationType.ComplectationCode:
                if (form.value.model_id !== undefined) {
                    getComplectationsByModelId(form.value.model_id).then((complectations: Complectation[]) => {
                        complectationOptions.value = complectations.map((complectation: Complectation) => {
                            return {
                                label: complectation.code,
                                value: complectation.id,
                            };
                        });
                    });
                }
                else {
                    complectationOptions.value = [];
                    form.value.complectation_id = undefined;
                }
                break;
            case CarUIFormComplectationType.CustomComplectation:
                complectationOptions.value = [];
                form.value.complectation_id = undefined;
                break;
        }
    }

    function fetchFormOptions(): void {
        getAllActiveModels().then((models: Model[]) => {
            arModels.value = models;
        });

        (function (): void {
            const options: SelectOption[] = [];
            for (let year = CarMaxProductionYear; year >= CarMinProductionYear; year--) {
                options.push({
                    label: `${year}`,
                    value: year,
                });
            }

            productionYearOptions.value = options;
        })();
    }

    onMounted(() => {
        fetchCar();
        fetchFormOptions();
    });
</script>

<style scoped lang="scss">
    @use './EditCar.scss' as *;
</style>
